import { useEffect, useRef } from 'react';

/**
 * Custom hook that subscribes to the global `periodChanged` event
 * (dispatched by Layout when user switches the active period from the sidebar).
 *
 * Optionally also listens to `settingsUpdated` for pages that depend on
 * global settings (e.g. operational cost overrides affecting Rekapan / Expenses).
 *
 * This centralises the `window.addEventListener('periodChanged', ...)` +
 * cleanup boilerplate that was previously duplicated across ~13 pages.
 *
 * @example
 *   // Simple: just refetch when period changes
 *   usePeriodChange(() => fetchData());
 *
 * @example
 *   // Access event.detail.periodId
 *   usePeriodChange((event) => {
 *     const periodId = event?.detail?.periodId;
 *     setSelectedPeriod(periodId);
 *     fetchBalanceSheet(periodId);
 *   });
 *
 * @example
 *   // Also listen to settingsUpdated
 *   usePeriodChange(() => fetchData(), { includeSettings: true });
 *
 * @param {(event?: CustomEvent) => void} handler - Invoked on period change.
 *   Always receives the latest closure (handler is kept in a ref so the
 *   effect can stay mounted across re-renders without stale data).
 * @param {Object} [options]
 * @param {boolean} [options.includeSettings=false] - Also fire handler on `settingsUpdated`.
 */
export const usePeriodChange = (handler, { includeSettings = false } = {}) => {
  const handlerRef = useRef(handler);

  // Keep ref updated so the listener always calls the latest handler
  // without requiring the consumer to add deps manually.
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    const listener = (event) => handlerRef.current?.(event);
    window.addEventListener('periodChanged', listener);
    if (includeSettings) {
      window.addEventListener('settingsUpdated', listener);
    }
    return () => {
      window.removeEventListener('periodChanged', listener);
      if (includeSettings) {
        window.removeEventListener('settingsUpdated', listener);
      }
    };
  }, [includeSettings]);
};

export default usePeriodChange;
